'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useTransition } from 'react'
import { SALE_SECTION_CONFIG, type SaleSectionSlug } from '@/saleSections'
import type { FilterPanelProps } from './FilterPanel'
import { buildSaleUrl, parseSaleSlugs } from '@/saleUrl'

export const useSaleFilterController = () => {
  const router = useRouter()
  const pathname = usePathname()
  const [isPending, startTransition] = useTransition()

  const pathSegments = pathname.split('/').filter(Boolean)
  const saleIndex = pathSegments.lastIndexOf('sale')
  const rawSlug =
    saleIndex >= 0
      ? (pathSegments[saleIndex + 1] as string | undefined)
      : undefined
  const slug: SaleSectionSlug =
    rawSlug && rawSlug in SALE_SECTION_CONFIG
      ? (rawSlug as SaleSectionSlug)
      : 'all'

  const slugs = saleIndex >= 0 ? pathSegments.slice(saleIndex + 2) : []
  const parsed = parseSaleSlugs(slugs)

  const saleConfig = SALE_SECTION_CONFIG[slug]
  const supportsStockFilter = saleConfig.supportsStockFilter

  const currentSort = parsed.sort
  const inStock = supportsStockFilter ? parsed.inStockOnly : false
  const exclusive = parsed.exclusiveOnly

  const pushTo = (nextUrl: string) => {
    startTransition(() => router.push(nextUrl))
  }

  const handleSortChange = (value: string) =>
    pushTo(
      buildSaleUrl({
        saleSlug: slug,
        filters: parsed.filters,
        sort: value,
        page: 1,
      })
    )

  const handleInStockChange = (checked: boolean) => {
    if (!supportsStockFilter) return
    const nextFilters = checked
      ? Array.from(new Set([...parsed.filters, 'in-stock']))
      : parsed.filters.filter(f => f !== 'in-stock')

    pushTo(
      buildSaleUrl({
        saleSlug: slug,
        filters: nextFilters,
        sort: parsed.sort,
        page: 1,
      })
    )
  }

  const handleExclusiveChange = (checked: boolean) => {
    const nextFilters = checked
      ? Array.from(new Set([...parsed.filters, 'exclusives']))
      : parsed.filters.filter(f => f !== 'exclusives')

    pushTo(
      buildSaleUrl({
        saleSlug: slug,
        filters: nextFilters,
        sort: parsed.sort,
        page: 1,
      })
    )
  }

  const handleCategoryChange = (targetSlug: string) => {
    if (!(targetSlug in SALE_SECTION_CONFIG)) {
      return
    }
    const nextSaleSlug = targetSlug as SaleSectionSlug
    const targetConfig = SALE_SECTION_CONFIG[nextSaleSlug]
    const nextFilters = targetConfig.supportsStockFilter
      ? parsed.filters
      : parsed.filters.filter(f => f !== 'in-stock')

    pushTo(
      buildSaleUrl({
        saleSlug: nextSaleSlug,
        filters: nextFilters,
        sort: parsed.sort,
        page: 1,
      })
    )
  }

  const getCategoryHref = (targetSlug: SaleSectionSlug) => {
    if (!(targetSlug in SALE_SECTION_CONFIG)) {
      return buildSaleUrl({
        saleSlug: 'all',
        filters: [],
        sort: parsed.sort,
        page: 1,
      })
    }
    const targetConfig = SALE_SECTION_CONFIG[targetSlug]
    const nextFilters = targetConfig.supportsStockFilter
      ? parsed.filters
      : parsed.filters.filter(f => f !== 'in-stock')

    return buildSaleUrl({
      saleSlug: targetSlug,
      filters: nextFilters,
      sort: parsed.sort,
      page: 1,
    })
  }

  return {
    slug,
    currentSort,
    inStock,
    exclusive,
    isPending,
    handleSortChange,
    handleInStockChange,
    handleExclusiveChange,
    handleCategoryChange,
    getCategoryHref,
  }
}

export const useSaleFilterPanelProps = (
  totalProducts: number
): FilterPanelProps => {
  const {
    slug,
    currentSort,
    inStock,
    exclusive,
    isPending,
    handleSortChange,
    handleInStockChange,
    handleExclusiveChange,
  } = useSaleFilterController()

  const saleConfig = SALE_SECTION_CONFIG[slug]
  const supportsStockFilter = saleConfig.supportsStockFilter

  return {
    categoryName: saleConfig.title,
    totalProducts,
    sort: currentSort,
    onSortChange: handleSortChange,
    isUpdating: isPending,
    productTypeLabel: saleConfig.resultCountLabel,
    toggles: [
      ...(supportsStockFilter
        ? [
            {
              id: 'in-stock',
              label: `Show in-stock ${saleConfig.sidebarLabel.toLowerCase()} first`,
              checked: inStock,
              onChange: handleInStockChange,
            },
          ]
        : []),
      {
        id: 'exclusive',
        label: 'Show Vanishing Inc. exclusives only',
        checked: exclusive,
        onChange: handleExclusiveChange,
      },
    ],
  }
}
